import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const WTK1Prelude1Viewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV846/wtk1-prelude1/wtk1-prelude1-a4.pdf';
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 10, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 10, 50));
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Prelude in C Major BWV 846.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => window.print();
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-10845">
      <Navbar data-id="element-10846" />
      <div className="pt-20" data-id="element-10847">
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-10848">
          <img src="https://images.unsplash.com/photo-1465847899084-d164df4dedc6?w=1200&h=400&fit=crop" alt="Prelude in C Major" className="w-full h-64 md:h-80 object-cover" data-id="element-10849" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-10850" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-10851">
            <div className="max-w-7xl mx-auto" data-id="element-10852">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-10853">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-10854" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-10855">
                Prelude in C Major
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-10856">
                BWV 846 · The Well-Tempered Clavier, Book I
              </p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-10857">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-10858">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-10859">
          <div className="flex items-center space-x-2" data-id="element-10860">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-10861">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-10862" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10863">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-10864">
                  <ZoomInIcon className="h-5 w-5" data-id="element-10865" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-10866">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-10867">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-10868" />
              <span className="hidden sm:inline" data-id="element-10869">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-10870">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-10871" />
              <span className="hidden sm:inline" data-id="element-10872">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-10873">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-10874">
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-10875">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Prelude in C Major Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-10876" />
          </div>
        </div>
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-10877">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10878">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-10879">
            <div data-id="element-10880">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10881">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-10882">
                <div data-id="element-10883">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10884">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10885">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-10886">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10887">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10888">
                    BWV 846
                  </dd>
                </div>
                <div data-id="element-10889">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10890">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10891">
                    C Major
                  </dd>
                </div>
                <div data-id="element-10892">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10893">
                    Collection
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10894">
                    The Well-Tempered Clavier, Book I
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-10895">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10896">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-10897">
                The Prelude in C Major is one of Bach's most beloved and
                recognizable keyboard works, opening the first book of the
                Well-Tempered Clavier. Its flowing arpeggiated texture creates a
                serene, harmonically rich atmosphere. The piece is an ideal
                study in voice leading and harmonic progression, and its
                deceptively simple surface conceals sophisticated harmonic
                movement that has captivated pianists and listeners for
                centuries.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-10898" />
    </div>;
};